import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Award, Printer } from "lucide-react";

export default function Reconocimientos() {
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [docentes, setDocentes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [docenteId, setDocenteId] = useState("");
  const [grupoId, setGrupoId] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [lugar, setLugar] = useState("Cd. y Pto. de Tampico, Tam.");

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/planteles"); setPlanteles(data); if (data.length) setPlantelId(data[0].id); }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  useEffect(() => { if (!plantelId) return; (async () => {
    try {
      const [staff, gs] = await Promise.all([
        api.get(`/planteles/${plantelId}/staff-list`),
        api.get("/grupos", { params: { plantel_id: plantelId } }),
      ]);
      setDocentes(staff.data.docentes || []);
      setGrupos(gs.data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [plantelId]);

  // Al elegir grupo, autoseleccionar docente titular
  useEffect(() => {
    if (!grupoId) return;
    const g = grupos.find((x) => x.id === grupoId);
    if (g?.docente_id) setDocenteId(g.docente_id);
  }, [grupoId, grupos]);

  const url = useMemo(() => {
    const p = new URLSearchParams();
    p.set("plantel_id", plantelId);
    if (grupoId) p.set("grupo_id", grupoId);
    if (docenteId) p.set("docente_id", docenteId);
    if (fecha) p.set("fecha", fecha);
    if (lugar) p.set("lugar", lugar);
    return `/reconocimientos/print?${p.toString()}`;
  }, [plantelId, grupoId, docenteId, fecha, lugar]);

  const canGen = plantelId && grupoId && docenteId;

  return (
    <AppLayout>
      <div className="max-w-2xl">
        <div className="mb-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Documentos imprimibles</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Reconocimientos a docentes</h1>
          <p className="text-sm text-[#545454] mt-1">Genera un reconocimiento tamaño carta horizontal por su labor durante el ciclo escolar.</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded p-5 space-y-3">
          {planteles.length > 1 && (
            <Field label="Plantel">
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="reco-plantel"><SelectValue /></SelectTrigger>
                <SelectContent>{planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Grupo">
            <Select value={grupoId} onValueChange={setGrupoId}>
              <SelectTrigger data-testid="reco-grupo"><SelectValue placeholder="Selecciona grupo…" /></SelectTrigger>
              <SelectContent>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Docente">
            <Select value={docenteId} onValueChange={setDocenteId}>
              <SelectTrigger data-testid="reco-docente"><SelectValue placeholder="Selecciona docente…" /></SelectTrigger>
              <SelectContent>
                {docentes.map((d) => <SelectItem key={d.id} value={d.id}>{[d.nombre, d.apellido_paterno, d.apellido_materno].filter(Boolean).join(" ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha"><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="reco-fecha" /></Field>
            <Field label="Ciudad / Lugar"><Input value={lugar} onChange={(e) => setLugar(e.target.value)} data-testid="reco-lugar" /></Field>
          </div>
          <div className="pt-2 flex justify-end border-t border-[#F3F4F6]">
            <Button disabled={!canGen} onClick={() => window.open(url, "_blank")} data-testid="btn-generar-reco" className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Award className="w-4 h-4 mr-1.5" /> Generar reconocimiento
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Field({ label, children }) {
  return (<div><label className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1 block">{label}</label>{children}</div>);
}
